import { Download, FileWarning } from 'lucide-react';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle, Spinner } from '@design-system';
import { usePolicyReviewDocumentDownload } from '../hooks/use-policy-review-document-download';

interface PolicyReviewDocumentPageProps {
  reviewId: string;
  documentIndex: string;
}

type DownloadStatus = 'preparing' | 'started' | 'failed';

const STATUS_COPY: Record<DownloadStatus, { title: string; description: string }> = {
  preparing: { title: 'Preparing your download…', description: 'This only takes a moment.' },
  started: { title: 'Your download has started', description: 'You can close this tab.' },
  failed: {
    title: 'We couldn’t open this document',
    description:
      'Only Waterlily team members can open policy review documents. Sign in with your Waterlily account and try the link again.',
  },
};

function StatusIcon({ status }: { status: DownloadStatus }) {
  if (status === 'failed') return <FileWarning />;
  if (status === 'preparing') return <Spinner />;
  return <Download />;
}

function downloadStatus(isLoading: boolean, isError: boolean): DownloadStatus {
  if (isError) return 'failed';
  if (isLoading) return 'preparing';
  return 'started';
}

/** Where the team's "Paid policy review" email links: downloads one document for Waterlily staff. */
export function PolicyReviewDocumentPage({ reviewId, documentIndex }: PolicyReviewDocumentPageProps) {
  const { isLoading, isError } = usePolicyReviewDocumentDownload(reviewId, Number(documentIndex));
  const status = downloadStatus(isLoading, isError);
  const { title, description } = STATUS_COPY[status];

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <StatusIcon status={status} />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    </main>
  );
}
